/** The wizard container: owns the reducer, the lookup loads and every write.
 *
 *  One component serves both routes. `/app/clients/new` mounts it in `create` mode, where the only
 *  live step is identity; creating the trainee navigates (replace) to
 *  `/app/clients/:userId/setup`, which mounts it again in `configure` mode. The URL is the state,
 *  so a refresh resumes instead of restarting, Back never lands on the identity form of a client
 *  that already exists, and the same flow doubles as "finish setting up" for any existing client.
 *
 *  Steps are module-level components rendered directly — never defined during render. See
 *  `WizardField.tsx` for the `insertBefore` failure that rule avoids. Keystrokes stay inside the
 *  reducer, and each step saves only when the coach presses המשך. */

import { useCallback, useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useMessageContext } from "../../../contexts/MessageContext";
import { getErrorMessage } from "../../../enums/ErrorMessages";
import { getTrainers } from "../../../services/TrainerService";
import { getUsersWithoutRoles } from "../../../services/UserService";
import { getTemplates } from "../../../services/TemplatesService";
import { listMenuTemplates } from "../../../services/NutritionV2Service";
import { getFormTemplates } from "../../../services/FormTemplateService";
import { checkEmailExists } from "../../../services/TraineeService";
import type { AdminNewTemplate } from "../../../api/generated";
import type { MenuTemplateV2 } from "../../nutrition/v2/model/nutritionV2Model";
import WizardShell from "./WizardShell";
import IdentityStep from "./steps/IdentityStep";
import GoalsStep from "./steps/GoalsStep";
import NutritionStep from "./steps/NutritionStep";
import WorkoutStep from "./steps/WorkoutStep";
import CheckInsFormsStep from "./steps/CheckInsFormsStep";
import SummaryStep from "./steps/SummaryStep";
import {
  commitNewClient,
  goalsFromWeek,
  loadCurrentWeek,
  saveCheckInsAndForms,
  saveGoals,
  saveNutrition,
  saveWorkout,
  type SaveResult,
} from "./setupSaves";
import {
  isCheckInsValid,
  isGoalsValid,
  isIdentityValid,
  isNutritionValid,
  isWorkoutValid,
} from "./stepValidation";
import {
  EMPTY_WORKOUT_FILTERS,
  initialWizardState,
  isFirstStep,
  stepOutcomes,
  navigableSteps,
  railSteps,
  wizardReducer,
  type ExistingUser,
  type StepId,
  type WizardMode,
  type WorkoutFilters,
} from "./wizardReducer";

/** Loads once, the first time its step is reached. Deliberately tiny: the app has no react-query,
 *  and the nutrition feature's `useAsyncList` is scoped to that feature. */
const useStepData = <T,>(enabled: boolean, load: () => Promise<T>, fallback: T) => {
  const [data, setData] = useState<T>(fallback);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || loaded) return;
    let alive = true;
    setLoading(true);
    load()
      .then(result => {
        if (alive) {
          setData(result);
          setLoaded(true);
        }
      })
      .catch(() => {
        // A failed lookup leaves the step empty with its "nothing found, you can skip" copy
        // rather than blocking the wizard on a list it does not strictly need.
        if (alive) setLoaded(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // `load` is re-created every render; re-running on its identity would loop forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, loaded]);

  return { data, loading };
};

/** The services re-throw in axios shape (`{response: {data}}`), and `errorCode` is numeric. */
const errorCodeOf = (error: unknown): number | undefined =>
  (error as { response?: { data?: { errorCode?: number } } })?.response?.data?.errorCode;

interface ClientSetupWizardProps {
  mode: WizardMode;
  traineeId?: number | null;
  traineeName?: string;
  traineeGender?: "MALE" | "FEMALE";
}

const ClientSetupWizard = ({
  mode,
  traineeId = null,
  traineeName,
  traineeGender,
}: ClientSetupWizardProps) => {
  const navigate = useNavigate();
  // The context is typed as possibly undefined (a component rendered outside the provider), so
  // every notification goes through optional access rather than a non-null assertion.
  const messages = useMessageContext();
  const { currentUser, isCompanyAdmin, isManager } = useAuth();

  const [state, dispatch] = useReducer(wizardReducer, undefined, () =>
    initialWizardState(mode, traineeId),
  );
  const [emailError, setEmailError] = useState<string | null>(null);

  const steps = railSteps();
  const current = state.current;
  const navigable = (() => {
    const all = navigableSteps(mode);
    if (mode === "configure") return all;
    // Create mode writes nothing, so stepping BACK to fix something is free. Stepping forward is
    // not offered: each step gates המשך on its own validity, and jumping past that would skip it.
    return all.slice(0, Math.max(all.indexOf(current), 0));
  })();
  const stepState = state.steps[current];
  const saving = stepState.status === "saving";
  const requiresTrainer = isCompanyAdmin() || isManager();

  // ── lookups, each loaded when its step is first reached ───────────────────────────────────
  // The generated DTOs make `id` optional on both of these. An entry without one cannot be
  // selected or submitted, so it is dropped here rather than defended against in the step.
  const hasId = <T extends { id?: number }>(entry: T): entry is T & { id: number } =>
    entry.id != null;

  const trainers = useStepData<Array<{ id: number; name?: string }>>(
    current === "identity" && requiresTrainer,
    () => getTrainers(currentUser?.companyId).then(res => (res.data ?? []).filter(hasId)),
    [],
  );
  const existingUsers = useStepData<ExistingUser[]>(
    current === "identity" && state.steps.identity.values.source === "existing",
    () =>
      getUsersWithoutRoles("TRAINEE", currentUser?.companyId).then(res =>
        (res.data ?? []).filter(hasId),
      ),
    [],
  );
  const menuTemplates = useStepData<MenuTemplateV2[]>(
    current === "nutrition",
    () => listMenuTemplates(),
    [],
  );
  // The workout list is filtered SERVER-side and re-fetched, rather than narrowed in the browser:
  // the endpoint is paged, so filtering a single fetched page would hide matching templates that
  // simply were not on it.
  const [workoutFilters, setWorkoutFilters] = useState<WorkoutFilters>(EMPTY_WORKOUT_FILTERS);
  const [workoutTemplateList, setWorkoutTemplateList] = useState<AdminNewTemplate[]>([]);
  const [workoutLoading, setWorkoutLoading] = useState(false);

  useEffect(() => {
    if (current !== "workout") return;
    let alive = true;
    setWorkoutLoading(true);
    // Debounced because `keyword` fires per keystroke; the dropdowns ride along harmlessly.
    const timer = setTimeout(() => {
      getTemplates(
        0,
        100,
        workoutFilters.numberOfWorkouts > 0 ? workoutFilters.numberOfWorkouts : null,
        workoutFilters.experienceLevel !== "ALL" ? workoutFilters.experienceLevel : null,
        workoutFilters.keyword || null,
        workoutFilters.bodyPart !== "ALL" ? workoutFilters.bodyPart : null,
        null,
        true,
        null,
        null,
        // Pinned to the client's own gender, exactly as `ChnageProgramDialog` does.
        traineeGender,
        null,
      )
        .then((res: {data?: {content?: AdminNewTemplate[]}}) => {
          if (alive) setWorkoutTemplateList(res.data?.content ?? []);
        })
        .catch(() => {
          if (alive) setWorkoutTemplateList([]);
        })
        .finally(() => {
          if (alive) setWorkoutLoading(false);
        });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [current, workoutFilters, traineeGender]);
  const formTemplates = useStepData<Array<{ id: number; name?: string }>>(
    current === "checkins",
    () => getFormTemplates(0, 100).then(res => res.data?.content ?? []),
    [],
  );

  // The goals step edits the week the server already has — see `GoalsValues.weekRow`.
  const [weekLoading, setWeekLoading] = useState(false);
  const needsWeek = current === "goals" && state.traineeId != null && !state.steps.goals.values.weekRow;
  useEffect(() => {
    if (!needsWeek || state.traineeId == null) return;
    let alive = true;
    setWeekLoading(true);
    loadCurrentWeek(state.traineeId)
      .then(week => {
        if (alive && week) dispatch({ type: "hydrate", steps: { goals: { values: goalsFromWeek(week) } } });
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setWeekLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [needsWeek, state.traineeId]);

  // ── writes ────────────────────────────────────────────────────────────────────────────────
  const runSave = useCallback(
    async (step: StepId, write: () => Promise<SaveResult>) => {
      dispatch({ type: "saveStart", step });
      try {
        const { warning } = await write();
        dispatch({ type: "saveSuccess", step });
        if (warning) messages?.notificationHandler.warning(warning);
      } catch (error) {
        dispatch({ type: "saveFailure", step, error: getErrorMessage(errorCodeOf(error)) });
      }
    },
    [messages],
  );

  /**
   * Leaving the identity step in create mode. Nothing is written — only the email is checked, so a
   * taken address is caught on the field now rather than after the coach has filled in five more
   * steps. The create itself can still 409 at commit time (a race, or a user who exists but is not
   * yet a trainee), and `handleCommit` puts that error back on this step.
   */
  const handleIdentityNext = async () => {
    const values = state.steps.identity.values;
    setEmailError(null);
    if (values.source !== "new") {
      dispatch({ type: "advance", step: "identity" });
      return;
    }
    dispatch({ type: "saveStart", step: "identity" });
    try {
      const taken = await checkEmailExists(values.email.trim());
      if (taken?.data) {
        setEmailError("אימייל כבר קיים במערכת");
        dispatch({ type: "saveFailure", step: "identity", error: "אימייל כבר קיים במערכת" });
        return;
      }
      dispatch({ type: "advance", step: "identity" });
    } catch (error) {
      dispatch({ type: "saveFailure", step: "identity", error: getErrorMessage(errorCodeOf(error)) });
    }
  };

  /**
   * The create-mode commit: creates the client and applies everything, in that order, because
   * `POST /admin/trainee` is what sends the welcome email and there is no way to send it later.
   *
   * On a clean run the coach lands on the client's page. If any assignment failed, they land in
   * the setup wizard for that client instead, where the per-step saves let them retry just the
   * parts that did not land — the client exists either way, so there is nothing to roll back.
   */
  const handleCommit = async () => {
    dispatch({ type: "commitStart" });
    try {
      const { traineeId: newId, results } = await commitNewClient(
        {
          identity: state.steps.identity.values,
          goals: state.steps.goals,
          nutrition: state.steps.nutrition,
          workout: state.steps.workout,
          checkins: state.steps.checkins,
        },
        {
          menuTemplateName: id => menuTemplates.data.find(entry => entry.id === id)?.name,
          workoutTemplate: id => workoutTemplateList.find(entry => entry.id === id) ?? null,
          clientName: state.steps.identity.values.name.trim(),
        },
      );

      dispatch({ type: "commitFinished", traineeId: newId, results });

      const failed = Object.values(results).filter(result => result?.status === "failed").length;
      if (failed > 0) {
        messages?.notificationHandler.warning(`הלקוח נוצר, אך ${failed} שלבים לא הוגדרו`);
        navigate(`/app/clients/${newId}/setup`, { replace: true });
      } else {
        messages?.notificationHandler.success("הלקוח נוצר וההגדרות הוחלו");
        navigate(`/app/clients/${newId}`, { replace: true });
      }
    } catch (error) {
      // Only the create itself can land here; without a client there is nothing to configure.
      const message = getErrorMessage(errorCodeOf(error));
      dispatch({ type: "saveFailure", step: "identity", error: message });
      dispatch({ type: "goTo", step: "identity" });
    }
  };

  const finish = () => {
    if (state.traineeId != null) navigate(`/app/clients/${state.traineeId}`);
    else navigate("/app/clients");
  };

  const exit = () => navigate(state.traineeId != null ? `/app/clients/${state.traineeId}` : "/app/clients");

  const handlePrimary = () => {
    const id = state.traineeId;

    // Create mode writes nothing until the summary — see `commitNewClient` for why.
    if (mode === "create") {
      if (current === "identity") return handleIdentityNext();
      if (current === "summary") return handleCommit();
      return dispatch({ type: "advance", step: current });
    }

    switch (current) {
      case "goals":
        return id == null ? undefined : runSave("goals", () => saveGoals(id, state.steps.goals.values));
      case "nutrition": {
        if (id == null) return undefined;
        const values = state.steps.nutrition.values;
        const template = menuTemplates.data.find(entry => entry.id === values.templateId);
        return runSave("nutrition", () => saveNutrition(id, values, template?.name));
      }
      case "workout": {
        if (id == null) return undefined;
        const values = state.steps.workout.values;
        const template =
          workoutTemplateList.find(entry => entry.id === values.templateId) ?? null;
        return runSave("workout", () => saveWorkout(id, values, template));
      }
      case "checkins":
        return id == null
          ? undefined
          : runSave("checkins", () =>
              saveCheckInsAndForms(id, state.steps.checkins.values, traineeName),
            );
      case "summary":
      default:
        return finish();
    }
  };

  const primaryDisabled = (() => {
    switch (current) {
      case "identity":
        return !isIdentityValid(state.steps.identity.values, requiresTrainer);
      case "goals":
        return !isGoalsValid(state.steps.goals.values, mode === "configure");
      case "nutrition":
        return !isNutritionValid(state.steps.nutrition.values);
      case "workout":
        return !isWorkoutValid(state.steps.workout.values);
      case "checkins":
        return !isCheckInsValid(state.steps.checkins.values);
      default:
        return false;
    }
  })();

  const primaryLabel = (() => {
    if (mode === "create") {
      if (current === "summary") return "צור לקוח וסיים";
      return "המשך";
    }
    if (current === "summary") return "סיום";
    return "שמור והמשך";
  })();

  const renderStep = () => {
    switch (current) {
      case "identity":
        return (
          <IdentityStep
            values={state.steps.identity.values}
            error={state.steps.identity.error}
            emailError={emailError}
            trainers={trainers.data}
            existingUsers={existingUsers.data}
            usersLoading={existingUsers.loading}
            requiresTrainer={requiresTrainer}
            onChange={values => {
              if (values.email !== undefined) setEmailError(null);
              dispatch({ type: "patch", step: "identity", values });
            }}
          />
        );
      case "goals":
        return (
          <GoalsStep
            values={state.steps.goals.values}
            error={state.steps.goals.error}
            loading={weekLoading}
            onChange={values => dispatch({ type: "patch", step: "goals", values })}
          />
        );
      case "nutrition":
        return (
          <NutritionStep
            values={state.steps.nutrition.values}
            error={state.steps.nutrition.error}
            templates={menuTemplates.data}
            loading={menuTemplates.loading}
            onChange={values => dispatch({ type: "patch", step: "nutrition", values })}
          />
        );
      case "workout":
        return (
          <WorkoutStep
            values={state.steps.workout.values}
            error={state.steps.workout.error}
            templates={workoutTemplateList}
            loading={workoutLoading}
            filters={workoutFilters}
            onFiltersChange={patch => setWorkoutFilters(previous => ({ ...previous, ...patch }))}
            onChange={values => dispatch({ type: "patch", step: "workout", values })}
          />
        );
      case "checkins":
        return (
          <CheckInsFormsStep
            values={state.steps.checkins.values}
            error={state.steps.checkins.error}
            formTemplates={formTemplates.data}
            loading={formTemplates.loading}
            onChange={values => dispatch({ type: "patch", step: "checkins", values })}
          />
        );
      case "summary":
      default:
        return (
          <SummaryStep
            state={state}
            rows={stepOutcomes(state)}
            traineeName={traineeName}
            onJumpTo={step => dispatch({ type: "goTo", step })}
          />
        );
    }
  };

  return (
    <WizardShell
      title={mode === "create" ? "לקוח חדש" : `השלמת הגדרות${traineeName ? ` — ${traineeName}` : ""}`}
      subtitle={
        mode === "create"
          ? "אחרי יצירת הלקוח נמשיך להגדרת היעדים, התפריט והתוכנית"
          : "אפשר להגדיר כל שלב בנפרד, ולדלג על מה שלא רלוונטי"
      }
      steps={steps}
      navigable={navigable}
      canNavigate={mode === "create" ? true : state.traineeId != null}
      state={state}
      primaryLabel={primaryLabel}
      primaryDisabled={primaryDisabled || saving}
      primaryLoading={saving}
      onBack={isFirstStep(state) ? undefined : () => dispatch({ type: "back" })}
      onSkip={
        current === "identity" || current === "summary"
          ? undefined
          : () => dispatch({ type: "skip", step: current })
      }
      onPrimary={handlePrimary}
      onExit={exit}
      onSelectStep={step => dispatch({ type: "goTo", step })}
    >
      {renderStep()}
    </WizardShell>
  );
};

export default ClientSetupWizard;
